import { useState } from 'react';
import { useApiKeys } from '../hooks/useApiKeys';
import { useAuth } from '../hooks/useAuth';

export default function ApiKeysPanel() {
  const { keys, loading, error, create, remove, toggle } = useApiKeys();
  const { user, login } = useAuth();
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKey, setShowNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  const handleCreate = async () => {
    if (!newKeyName.trim() || isCreating) return;
    if (!user) {
      const signedInUser = await login();
      if (!signedInUser) return;
    }
    
    setIsCreating(true);
    try {
      const key = await create(newKeyName);
      setShowNewKey(key.key);
      setNewKeyName('');
    } catch (err) {
      // Error handled by hook
    } finally {
      setIsCreating(false);
    }
  };
  
  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      console.error('Failed to copy');
    }
  };
  
  const handleDelete = async (keyId: string) => {
    if (!user) {
      const signedInUser = await login();
      if (!signedInUser) return;
    }
    if (!confirm('Are you sure you want to delete this API key?')) return;
    await remove(keyId);
  };
  
  return (
    <div className="bg-white border border-[#e5e5e5] rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-lg bg-[#f5f5f5] flex items-center justify-center">
          <svg className="w-4 h-4 text-[#8a6a2f]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-[#0a0a0a]">API Keys</h2>
      </div>
      
      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!user && (
        <div className="mb-4 p-3 bg-[#f5f5f5] border border-[#e5e5e5] rounded-lg">
          <p className="text-sm text-[#525252]">Sign in to generate and manage API keys.</p>
        </div>
      )}
      
      {/* New key notification */}
      {showNewKey && (
        <div className="mb-5 p-4 bg-[#f5f5f5] border border-[#8a6a2f]/30 rounded-lg animate-fade-in">
          <p className="text-sm text-[#8a6a2f] mb-2 font-medium">✓ Key created! Copy it now — you won't see it again.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white p-2.5 rounded-lg font-mono text-[#0a0a0a] break-all border border-[#e5e5e5]">
              {showNewKey}
            </code>
            <button 
              onClick={() => { copyKey(showNewKey); setShowNewKey(null); }} 
              className="p-2.5 bg-[#8a6a2f] text-white rounded-lg hover:bg-[#a67c3a] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {/* Create key input */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={newKeyName}
          onChange={e => setNewKeyName(e.target.value)}
          placeholder="Key name..."
          className="flex-1 px-3 py-2 bg-white border border-[#d4d4d4] rounded-lg text-sm text-[#0a0a0a] placeholder-[#a3a3a3] focus:outline-none focus:border-[#8a6a2f] transition-colors"
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          disabled={isCreating}
        />
        <button 
          onClick={handleCreate}
          disabled={!newKeyName.trim() || keys.length >= 3 || isCreating}
          className="px-4 py-2 bg-[#0a0a0a] text-white text-sm font-medium rounded-lg hover:bg-[#262626] disabled:opacity-50 disabled:hover:bg-[#0a0a0a] transition-colors"
        >
          {isCreating ? '...' : 'Create'}
        </button>
      </div>
      
      {/* Keys list */}
      <div className="space-y-2">
        {loading ? (
          <div className="py-6 text-center">
            <div className="w-6 h-6 border-2 border-[#e5e5e5] border-t-[#8a6a2f] rounded-full animate-spin mx-auto" />
          </div>
        ) : keys.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-[#737373]">No API keys yet</p>
            <p className="text-xs text-[#a3a3a3] mt-1">Create one to start using the API</p>
          </div>
        ) : (
          keys.map(key => (
            <div 
              key={key.id} 
              className="flex items-center gap-3 p-3 bg-[#f5f5f5] hover:bg-[#e5e5e5] rounded-lg group transition-colors"
            >
              {/* Status */}
              <div className={`w-2 h-2 rounded-full ${key.active ? 'bg-green-500' : 'bg-[#a3a3a3]'}`} />
              
              {/* Key info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0a0a0a] truncate">{key.name}</p>
                <code className="text-xs text-[#737373] font-mono">{key.key.slice(0, 16)}...</code>
              </div>
              
              {/* Copy */}
              <button 
                onClick={() => copyKey(key.key)}
                className="p-1.5 text-[#737373] hover:text-[#0a0a0a] hover:bg-white rounded-lg opacity-0 group-hover:opacity-100 transition-all"
              >
                {copied === key.key ? (
                  <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
              
              {/* Toggle */}
              <button 
                onClick={async () => {
                  if (!user) {
                    const signedInUser = await login();
                    if (!signedInUser) return;
                  }
                  await toggle(key.id!, !key.active);
                }}
                className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                  key.active 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-[#e5e5e5] text-[#737373] hover:bg-[#d4d4d4]'
                }`}
              >
                {key.active ? 'Active' : 'Off'}
              </button>
              
              {/* Delete */}
              <button 
                onClick={() => handleDelete(key.id!)}
                className="p-1.5 text-[#737373] hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
      
      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
        <p className="text-xs text-[#a3a3a3]">
          {keys.length}/3 keys (free tier)
        </p>
      </div>
    </div>
  );
}
